/**
 * LinkedCampaignsList — "Campaigns with this flow": the tracker's cached
 * campaign list filtered to the ones that route to this flow id.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo, type FC } from 'react';

import { LoadingSpinner } from '@/components/ui/feedback/LoadingSpinner';
import FlowMetaFieldLabel from '@/features/flow-optimizer/tabs/standard/v2/sections/flow-meta/FlowMetaFieldLabel';

interface LinkableCampaign {
  id: string;
  name: string;
  flowId?: string | null;
  status?: string;
}

export interface LinkedCampaignsListProps {
  flowId: string;
  campaigns: readonly LinkableCampaign[] | undefined;
  isLoading: boolean;
  trackerName: string;
}

const LinkedCampaignsList: FC<LinkedCampaignsListProps> = ({
  flowId,
  campaigns,
  isLoading,
  trackerName,
}) => {
  const linked = useMemo(
    () => (campaigns ?? []).filter((c) => c.flowId === flowId && c.status !== 'deleted'),
    [campaigns, flowId],
  );
  return (
    <div className="space-y-1.5">
      <FlowMetaFieldLabel label="Campaigns with this flow" />
      {isLoading ? (
        <div className="flex justify-center py-2">
          <LoadingSpinner />
        </div>
      ) : linked.length === 0 ? (
        <p className="text-[11px] italic leading-snug text-muted-foreground">
          No campaigns from the cached {trackerName} list reference this flow id.
        </p>
      ) : (
        <ul className="list-inside list-disc space-y-0.5 text-xs text-foreground">
          {linked.map((c) => (
            <li key={c.id}>{c.name}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LinkedCampaignsList;
